import {createAsyncThunk} from '@reduxjs/toolkit';
import {setIsMetaLoading} from '../templateMapping.slice';
import {CreativeTemplateSource} from 'common/src/modules/creativeTemplates/creativeTemplates.types';
import {getFormatKey} from 'common/src/components/PreviewPanelComponents/utils/getFormatKey';
import {handleApiError} from 'common/src/modules/errorHandling/handleApiError';
import {RootState} from 'common/src/redux/store';
import loadMetaFonts from 'common/src/modules/dynamicParameters/utils/loadMetaFonts';
import fetchFormatMetadata from 'common/src/modules/dynamicParameters/utils/fetchFormatMetadata';
import initialLoadTemplateMappingData from '../utils/initialLoadTemplateMappingData';
import refetchTemplateMappingData from '../utils/refetchTemplateMappingData';

export const loadTemplateMappingData = createAsyncThunk(
  `loadTemplateMappingData`,
  async (
    {templateId, changedFormats}: {templateId?: number; changedFormats?: string[]},
    {dispatch, getState},
  ): Promise<void> => {
    const state = getState() as RootState;
    const template = state.creativeTemplatePage.template;

    if (!template) {
      throw new Error('Template not found');
    }

    // Use templateId from parameter if provided, otherwise get from Redux state
    const effectiveTemplateId = templateId ?? template.id;

    // Get mappable formats
    const mappableFormats = template.formats
      ?.filter((f) => f.RendererVersion >= 10)
      .map(({format}) => ({w: format.Width, h: format.Height}));

    if (!mappableFormats?.length && !changedFormats) {
      // No mappable formats, set empty state
      initialLoadTemplateMappingData(dispatch, {
        template,
        templateMeta: {},
        templateMapping: {},
      });

      return;
    }

    const allFormatKeys = mappableFormats.map(getFormatKey);

    // Use changedFormats if provided, otherwise use all format keys
    const formatsToFetch = changedFormats || allFormatKeys;

    if (!formatsToFetch?.length) return;

    // Set loading state
    dispatch(setIsMetaLoading(true));

    try {
      // Fetch metadata for each format using common utility
      const isJetMapping = template.source === CreativeTemplateSource.AfterEffect;
      const {templateMeta, templateMapping, metadataResults} = await fetchFormatMetadata(
        effectiveTemplateId,
        formatsToFetch,
        !!isJetMapping,
      );

      // If this is a refetch (changedFormats provided), merge with existing data
      if (changedFormats) {
        refetchTemplateMappingData(dispatch, {
          templateMeta,
          templateMapping,
          currentState: state.creativeTemplatePage,
        });
      } else {
        // Initial load - set all data from scratch
        initialLoadTemplateMappingData(dispatch, {
          template,
          templateMeta,
          templateMapping,
        });
      }

      // Load fonts
      loadMetaFonts(metadataResults);
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch(setIsMetaLoading(false));
    }
  },
);
